import React, { useState } from 'react';
import { ArrowLeftRight, CheckCircle, AlertCircle, Building2, MapPin } from 'lucide-react';

interface Site {
  id: number;
  code: string;
  name: string;
  site_type: string;
  address: string;
  is_active: boolean;
  tenant: {
    id: number;
    name: string;
    code: string;
  };
}

interface SiteSelectorProps {
  sites: Site[];
  currentSite: Site | null;
  onSiteSwitch: (siteCode: string) => void;
}

const SiteSelector: React.FC<SiteSelectorProps> = ({ sites, currentSite, onSiteSwitch }) => {
  const [selectedSite, setSelectedSite] = useState<string>('');
  const [isSwitching, setIsSwitching] = useState(false);

  const handleSwitch = async () => {
    if (!selectedSite) return;
    
    setIsSwitching(true);
    try {
      await onSiteSwitch(selectedSite);
      setSelectedSite('');
    } finally {
      setIsSwitching(false);
    }
  };

  const getSiteTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      'INDUSTRIAL': 'Industriel',
      'STANDARD': 'Standard',
      'MOBILE': 'Mobile',
      'WAREHOUSE': 'Entrepôt'
    };
    return types[type] || type;
  };

  const getSiteTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'INDUSTRIAL': 'bg-blue-100 text-blue-800',
      'STANDARD': 'bg-green-100 text-green-800',
      'MOBILE': 'bg-purple-100 text-purple-800',
      'WAREHOUSE': 'bg-orange-100 text-orange-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <ArrowLeftRight className="w-6 h-6 text-orange-500 mr-3" />
          <h2 className="text-xl font-semibold text-gray-900">Sélecteur de Sites</h2>
        </div>
        <p className="text-gray-600">
          Changez de site actif pour accéder aux données et fonctionnalités spécifiques à ce site.
        </p>
      </div>

      {/* Site Actuel */}
      {currentSite && (
        <div className="mb-8 p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-orange-500 mr-3" />
            <div>
              <h3 className="font-medium text-orange-900">Site Actuel</h3>
              <p className="text-sm text-orange-700">
                {currentSite.name} ({currentSite.code}) - {currentSite.tenant.name}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sélection du nouveau site */}
      <div className="mb-6">
        <label htmlFor="site-select" className="block text-sm font-medium text-gray-700 mb-2">
          Choisir un nouveau site
        </label>
        <div className="flex space-x-3">
          <select
            id="site-select"
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            className="custom-select flex-1"
          >
            <option value="">Sélectionner un site...</option>
            {sites
              .filter(site => !currentSite || site.id !== currentSite.id)
              .map((site) => (
                <option key={site.id} value={site.code}>
                  {site.name} ({site.code})
                </option>
              ))}
          </select>
          <button
            onClick={handleSwitch}
            disabled={!selectedSite || isSwitching}
            className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isSwitching ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Changement...
              </>
            ) : (
              <>
                <ArrowLeftRight className="w-4 h-4 mr-2" />
                Changer
              </>
            )}
          </button>
        </div>
      </div>

      {/* Liste des sites disponibles */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Sites Disponibles</h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {sites.map((site) => (
            <div
              key={site.id}
              className={`p-4 border rounded-lg transition-all ${
                currentSite?.id === site.id
                  ? 'border-orange-300 bg-orange-50'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                  <Building2 className="w-5 h-5 text-gray-400 mr-2" />
                  <span className="font-medium text-gray-900">{site.name}</span>
                </div>
                {currentSite?.id === site.id && (
                  <CheckCircle className="w-5 h-5 text-orange-500" />
                )}
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center text-sm text-gray-600">
                  <span className="font-medium mr-2">Code:</span>
                  <span className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">
                    {site.code}
                  </span>
                </div>
                
                <div className="flex items-center text-sm text-gray-600">
                  <span className="font-medium mr-2">Type:</span>
                  <span className={`px-2 py-1 rounded text-xs ${getSiteTypeColor(site.site_type)}`}>
                    {getSiteTypeLabel(site.site_type)}
                  </span>
                </div>
                
                <div className="flex items-center text-sm text-gray-600">
                  <MapPin className="w-4 h-4 mr-2" />
                  <span className="truncate">{site.address}</span>
                </div>
                
                <div className="flex items-center text-sm text-gray-600">
                  <span className="font-medium mr-2">Tenant:</span>
                  <span className="text-gray-700">{site.tenant.name}</span>
                </div>
                
                <div className="flex items-center text-sm">
                  <span className="font-medium mr-2">Statut:</span>
                  {site.is_active ? (
                    <span className="flex items-center text-green-600">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Actif
                    </span>
                  ) : (
                    <span className="flex items-center text-red-600">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      Inactif
                    </span>
                  )}
                </div>
              </div>
              
              {currentSite?.id !== site.id && (
                <button
                  onClick={() => setSelectedSite(site.code)}
                  className="mt-3 w-full px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Sélectionner ce site
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {sites.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun site disponible</h3>
          <p className="text-gray-600">
            Vous n'avez pas encore accès à des sites ou aucun site n'a été créé.
          </p>
        </div>
      )}
    </div>
  );
};

export default SiteSelector;
